import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import { Search, MoreHorizontal, UserX, Mail, Shield, UserCog, RefreshCw, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { useUsers } from "@/hooks/useUsers";
import { type UserRole, ROLE_HIERARCHY } from "@/types/access";
import { Skeleton } from "@/components/ui/skeleton";
import { TopUpQuotaDialog } from "@/components/users/TopUpQuotaDialog";
import { Coins } from "lucide-react";

export function UsersPage() {
  const { users, loading, error, updateUserRole, refresh, page, setPage, total, limit } = useUsers();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRoleFilter, setSelectedRoleFilter] = useState<string>("all");
  const [selectedUserForQuota, setSelectedUserForQuota] = useState<{ id: string; name: string } | null>(null);
  const [isQuotaDialogOpen, setIsQuotaDialogOpen] = useState(false);

  const filteredUsers = users.filter((user) => {
    const searchLower = searchQuery.toLowerCase();
    const nameMatch = 
      (user.firstName && user.firstName.toLowerCase().includes(searchLower)) ||
      (user.lastName && user.lastName.toLowerCase().includes(searchLower)) ||
      (user.email && user.email.toLowerCase().includes(searchLower));
    
    if (selectedRoleFilter === "all") return nameMatch;
    return nameMatch && user.role === selectedRoleFilter;
  });

  const getRoleBadge = (role?: UserRole) => {
    switch (role) {
      case "super_admin":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100 hover:bg-purple-100";
      case "admin":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100 hover:bg-blue-100";
      case "user":
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100 hover:bg-gray-100";
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    await updateUserRole(userId, newRole as UserRole);
  };



  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[400px] gap-4">
        <p className="text-red-500">Error: {error}</p>
        <Button onClick={refresh} variant="outline">Retry</Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-9 w-[150px]" />
              <Skeleton className="h-4 w-[250px]" />
            </div>
          ) : (
            <>
              <h2 className="text-3xl font-bold tracking-tight">Users</h2>
              <p className="text-muted-foreground">
                Manage platform users and their roles
              </p>
            </>
          )}
        </div>
        {/* <div className="flex gap-2">
          {loading ? (
            <>
              <Skeleton className="h-10 w-10" />
              <Skeleton className="h-10 w-[100px]" />
              <Skeleton className="h-10 w-[110px]" />
            </>
          ) : (
            <>
              <Button variant="outline" size="icon" onClick={refresh} title="Refresh Users">
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button variant="outline">
                <Mail className="mr-2 h-4 w-4" />
                Email All
              </Button>
              <Button>Export Users</Button>
            </>
          )}
        </div> */}
      </div>

      {/* Stats Cards - Simplified for now as we don't have all stats from API yet */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            {loading ? (
              <div className="space-y-2">
                <Skeleton className="h-8 w-[60px]" />
                <Skeleton className="h-3 w-[80px]" />
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold">{users.length}</div>
                <p className="text-xs text-muted-foreground">Total Users</p>
              </>
            )}
          </CardContent>
        </Card>
         <Card>
          <CardContent className="p-4">
            {loading ? (
              <div className="space-y-2">
                <Skeleton className="h-8 w-[40px]" />
                <Skeleton className="h-3 w-[60px]" />
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold">{users.filter(u => u.role === 'admin' || u.role === 'super_admin').length}</div>
                <p className="text-xs text-muted-foreground">Admins</p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4">
            {loading ? (
              <>
                <Skeleton className="h-10 flex-1" />
                <div className="flex gap-2">
                  <Skeleton className="h-9 w-[50px]" />
                  <Skeleton className="h-9 w-[80px]" />
                  <Skeleton className="h-9 w-[110px]" />
                </div>
              </>
            ) : (
              <>
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search users by name or email..."
                    className="pl-8"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant={selectedRoleFilter === "all" ? "default" : "outline"} 
                    size="sm"
                    onClick={() => setSelectedRoleFilter("all")}
                  >
                    All
                  </Button>
                   <Button 
                    variant={selectedRoleFilter === "admin" ? "default" : "outline"} 
                    size="sm"
                    onClick={() => setSelectedRoleFilter("admin")}
                  >
                    Admins
                  </Button>
                  <Button 
                    variant={selectedRoleFilter === "user" ? "default" : "outline"} 
                    size="sm"
                    onClick={() => setSelectedRoleFilter("user")}
                  >
                    Regular Users
                  </Button>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Users</CardTitle>
          <CardDescription>
            Showing {filteredUsers.length} of {users.length} users
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead>Last Active</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && users.length === 0 ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-8 w-8 rounded-full" />
                        <div className="space-y-1">
                          <Skeleton className="h-4 w-[150px]" />
                          <Skeleton className="h-3 w-[100px]" />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-5 w-[80px] rounded-full" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-[100px]" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-[100px]" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-8 w-8 rounded-md" />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <img 
                        src={user.imageUrl} 
                        alt={`${user.firstName} ${user.lastName}`} 
                        className="h-8 w-8 rounded-full bg-gray-100"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${user.firstName}+${user.lastName}&background=random`;
                        }} 
                      />
                      <div>
                        <p className="font-medium">
                          {user.firstName} {user.lastName} 
                          {!user.firstName && !user.lastName && "Unknown User"}
                        </p>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className={getRoleBadge(user.role)}>
                      {user.role || 'user'}
                    </Badge>
                  </TableCell>
                  <TableCell>{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    {user.lastSignInAt 
                      ? new Date(user.lastSignInAt).toLocaleDateString() 
                      : "Never"}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuSeparator />

                        <DropdownMenuItem onClick={() => {
                          setSelectedUserForQuota({ 
                            id: user.id, 
                            name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email 
                          });
                          setIsQuotaDialogOpen(true);
                        }}>
                          <Coins className="mr-2 h-4 w-4" />
                          Top Up Quota
                        </DropdownMenuItem>
                        
                        {/* <DropdownMenuItem>
                          <Shield className="mr-2 h-4 w-4" />
                          View Details
                        </DropdownMenuItem> */}
                        
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger>
                            <UserCog className="mr-2 h-4 w-4" />
                            Change Role
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent>
                            <DropdownMenuRadioGroup value={user.role || 'user'} onValueChange={(value) => handleRoleChange(user.id, value)}>
                              {ROLE_HIERARCHY.map((role) => (
                                <DropdownMenuRadioItem key={role} value={role}>
                                  {role.charAt(0).toUpperCase() + role.slice(1).replace('_', ' ')}
                                </DropdownMenuRadioItem>
                              ))}
                            </DropdownMenuRadioGroup>
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>

                        {/* <DropdownMenuItem>
                          <Mail className="mr-2 h-4 w-4" />
                          Send Email
                        </DropdownMenuItem> */}
                        {/* <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-red-600">
                          <UserX className="mr-2 h-4 w-4" />
                          Suspend User
                        </DropdownMenuItem> */}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>


      {/* Pagination */}
      <div className="flex items-center justify-end space-x-2 py-4">
        <div className="flex-1 text-sm text-muted-foreground">
          Showing {users.length > 0 ? (page - 1) * limit + 1 : 0} to {Math.min(page * limit, total)} of {total} users
        </div>
        <div className="space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(1)}
            disabled={page === 1 || loading}
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => p + 1)}
            disabled={page * limit >= total || loading}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
           <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(Math.ceil(total / limit))}
            disabled={page * limit >= total || loading}
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <TopUpQuotaDialog 
        userId={selectedUserForQuota?.id || null}
        userName={selectedUserForQuota?.name}
        isOpen={isQuotaDialogOpen}
        onClose={() => setIsQuotaDialogOpen(false)}
        onSuccess={() => {
            refresh();
        }}
      />
    </div>
  );
}

